import { create } from 'zustand';
import api from '@/lib/api';
import type {
 JobProfile,
 CreateJobProfileData,
 ParsedJobData,
 ApiResponse,
} from '@/types/jobProfile';

interface JobProfileState {
 profiles: JobProfile[];
 currentProfile: JobProfile | null;
 isLoading: boolean;
 error: string | null;

 // Actions
 fetchProfiles: () => Promise<void>;
 fetchProfile: (id: string) => Promise<void>;
 createProfile: (data: CreateJobProfileData) => Promise<JobProfile>;
 updateProfile: (
  id: string,
  data: Partial<CreateJobProfileData>
 ) => Promise<JobProfile>;
 deleteProfile: (id: string) => Promise<void>;
 parseJobUrl: (url: string) => Promise<ParsedJobData>;
 setCurrentProfile: (profile: JobProfile | null) => void;
}

export const useJobProfileStore = create<JobProfileState>((set) => ({
 profiles: [],
 currentProfile: null,
 isLoading: false,
 error: null,

 fetchProfiles: async () => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.get<ApiResponse<JobProfile[]>>('/profiles');
   set({ profiles: response.data.data, isLoading: false });
  } catch (error: any) {
   set({
    error: error.response?.data?.error?.message || 'Failed to fetch profiles',
    isLoading: false,
   });
  }
 },

 fetchProfile: async (id) => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.get<ApiResponse<JobProfile>>(`/profiles/${id}`);
   set({ currentProfile: response.data.data, isLoading: false });
  } catch (error: any) {
   set({
    error: error.response?.data?.error?.message || 'Failed to fetch profile',
    isLoading: false,
   });
  }
 },

 createProfile: async (data) => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.post<ApiResponse<JobProfile>>('/profiles', data);
   const newProfile = response.data.data;
   set((state) => ({
    profiles: [...state.profiles, newProfile],
    isLoading: false,
   }));
   return newProfile;
  } catch (error: any) {
   set({
    error: error.response?.data?.error?.message || 'Failed to create profile',
    isLoading: false,
   });
   throw error;
  }
 },

 updateProfile: async (id, data) => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.put<ApiResponse<JobProfile>>(
    `/profiles/${id}`,
    data
   );
   const updatedProfile = response.data.data;
   set((state) => ({
    profiles: state.profiles.map((p) => (p.id === id ? updatedProfile : p)),
    currentProfile:
     state.currentProfile?.id === id ? updatedProfile : state.currentProfile,
    isLoading: false,
   }));
   return updatedProfile;
  } catch (error: any) {
   set({
    error: error.response?.data?.error?.message || 'Failed to update profile',
    isLoading: false,
   });
   throw error;
  }
 },

 deleteProfile: async (id) => {
  set({ isLoading: true, error: null });
  try {
   await api.delete(`/profiles/${id}`);
   set((state) => ({
    profiles: state.profiles.filter((p) => p.id !== id),
    currentProfile:
     state.currentProfile?.id === id ? null : state.currentProfile,
    isLoading: false,
   }));
  } catch (error: any) {
   set({
    error: error.response?.data?.error?.message || 'Failed to delete profile',
    isLoading: false,
   });
   throw error;
  }
 },

 parseJobUrl: async (url) => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.post<ApiResponse<ParsedJobData>>(
    '/profiles/parse-url',
    { url }
   );
   set({ isLoading: false });
   return response.data.data;
  } catch (error: any) {
   set({
    error: error.response?.data?.error?.message || 'Failed to parse job URL',
    isLoading: false,
   });
   throw error;
  }
 },

 setCurrentProfile: (profile) => set({ currentProfile: profile }),
}));
